import React, { useEffect, useState } from "react";
import { useBreadcrumbs } from "~/main-layout/BreadcrumbProvider";
import { VisibilityListener } from "~/visibility-listener";
import useInfiniteScroll from "~/components/search-page-wrapper/infinite-scroll/useInfiniteScroll";
import { SearchFilterContainer } from "~/components/search-filter-container";
import {
  SearchPageWrapper,
  SearchListContent,
} from "~/components/search-page-wrapper";
import { FilterForm } from "./FilterForm";
import { TrainingReportResults } from "./TrainingReportResults";
import { TrainingReportFilterOptions } from "./types";
import {
  GetTrainingReportQuery,
  useGetTrainingReportQuery,
} from "./getTrainingReport.hotchoc.generated";

type TrainingRecord =
  GetTrainingReportQuery["training"]["report"]["records"][number];

const Breadcrumbs: React.FC = () => {
  useBreadcrumbs([{ text: "Training Report" }], []);
  return <></>;
};

type LoadRecordsProps = {
  dispatch: React.Dispatch<any>;
  tag: "DataLoaded" | "MoreDataLoaded";
  token: string | null;
  filterOptions: TrainingReportFilterOptions;
};

const LoadRecords: React.FC<LoadRecordsProps> = ({
  dispatch,
  tag,
  filterOptions,
  token,
}) => {
  const { data } = useGetTrainingReportQuery({
    variables: {
      input: {
        after: filterOptions.after,
        before: filterOptions.before,
        employeeSearch: filterOptions.employeeSearch,
        trainingType: filterOptions.trainingType,
        workLocation: filterOptions.workLocation,
        token,
      },
    },
  });

  useEffect(() => {
    if (data) {
      const result = data.training.report;
      dispatch({
        tag,
        items: result.records as TrainingRecord[],
        searchToken: result.token ?? null,
      });
    }
  }, [data]);

  return <></>;
};

export const TrainingReportPage: React.FC = () => {
  const [filters, setFilters] = useState<TrainingReportFilterOptions | null>(
    null
  );

  return (
    <SearchPageWrapper>
      <SearchFilterContainer>
        <FilterForm onFiltersChanged={setFilters} />
      </SearchFilterContainer>
      {filters && <TrainingReportContent filters={filters} />}
    </SearchPageWrapper>
  );
};

type TrainingReportContentProps = {
  filters: TrainingReportFilterOptions;
};

const TrainingReportContent: React.FC<TrainingReportContentProps> = ({
  filters,
}) => {
  const initialState = {
    tag: "Loading",
    items: [] as TrainingRecord[],
    showVisibility: false,
    loading: true,
    filterOptions: filters,
    searchToken: null,
    selectedItems: [],
    selectIdKey: "",
  };

  const { state, dispatch } = useInfiniteScroll<
    TrainingRecord,
    TrainingReportFilterOptions
  >(initialState);

  useEffect(() => {
    dispatch({
      tag: "SearchCriteriaChanged",
      filterOptions: filters,
    });
  }, [filters]);

  return (
    <SearchListContent
      loading={state.loading}
      loadingMore={state.tag === "LoadingMore"}
    >
      <Breadcrumbs />
      {state.tag === "Loading" && (
        <LoadRecords
          dispatch={dispatch}
          tag="DataLoaded"
          filterOptions={state.filterOptions}
          token={null}
        />
      )}
      {state.tag === "LoadingMore" && (
        <LoadRecords
          dispatch={dispatch}
          tag="MoreDataLoaded"
          token={state.searchToken}
          filterOptions={state.filterOptions}
        />
      )}
      <TrainingReportResults records={state.items} loading={state.loading} />
      {state.showVisibility && (
        <VisibilityListener
          onShown={() => dispatch({ tag: "ScrolledToBottom" })}
        />
      )}
    </SearchListContent>
  );
};
